import { useEffect, useRef, useState } from 'react';
import { timelineEvents } from '@/mocks/programData';

function useInView<T extends HTMLElement = HTMLDivElement>(threshold = 0.2) {
  const ref = useRef<T>(null);
  const [isInView, setIsInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.unobserve(el);
        }
      },
      { threshold }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold]);

  return { ref, isInView };
}

function useScrollProgress<T extends HTMLElement = HTMLDivElement>() {
  const ref = useRef<T>(null);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const handleScroll = () => {
      const rect = el.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const elementHeight = rect.height;
      const start = rect.top - windowHeight;
      const scrollableDistance = elementHeight + windowHeight;
      const currentScroll = -start;
      const rawProgress = currentScroll / scrollableDistance;
      setProgress(Math.max(0, Math.min(1, rawProgress)));
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return { ref, progress };
}

export default function Timeline() {
  const { ref: sectionRef, progress } = useScrollProgress<HTMLDivElement>();
  const { ref: titleRef, isInView: titleInView } = useInView<HTMLDivElement>(0.3);

  const statusColors = {
    completed: 'bg-emerald-500',
    active: 'bg-accent',
    pending: 'bg-gray-300',
  };

  const statusLabels = {
    completed: 'Concluído',
    active: 'Em curso',
    pending: 'Pendente',
  };

  return (
    <section id="cronograma" className="py-20 md:py-28 bg-white">
      <div className="w-full px-4 md:px-8 lg:px-12">
        <div className="max-w-5xl mx-auto">
          <div
            ref={titleRef}
            className={`text-center mb-16 transition-all duration-700 ${
              titleInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
          >
            <span className="text-accent text-sm font-semibold uppercase tracking-wider">
              Cronograma
            </span>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-primary mt-3">
              Linha Temporal do Programa
            </h2>
            <div className="w-20 h-1 bg-accent mx-auto mt-4 rounded-full" />
          </div>

          <div ref={sectionRef} className="relative">
            {/* Base line (gray background) */}
            <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-gray-200 md:-translate-x-px" />

            {/* Animated fill line */}
            <div
              className="absolute left-4 md:left-1/2 top-0 w-0.5 bg-accent md:-translate-x-px transition-all duration-100 ease-out"
              style={{
                height: `${progress * 100}%`,
                boxShadow: '0 0 12px rgba(217, 119, 6, 0.4)',
              }}
            />

            <div className="space-y-8 md:space-y-12">
              {timelineEvents.map((event, i) => {
                const isLeft = i % 2 === 0;
                const isActive = event.status === 'active';
                const isCompleted = event.status === 'completed';

                return (
                  <TimelineItem
                    key={i}
                    event={event}
                    index={i}
                    isLeft={isLeft}
                    isActive={isActive}
                    isCompleted={isCompleted}
                    statusColors={statusColors}
                    statusLabels={statusLabels}
                  />
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

interface TimelineItemProps {
  event: {
    date: string;
    status: 'completed' | 'active' | 'pending';
    phase: string;
    description: string;
  };
  index: number;
  isLeft: boolean;
  isActive: boolean;
  isCompleted: boolean;
  statusColors: Record<string, string>;
  statusLabels: Record<string, string>;
}

function TimelineItem({
  event,
  index,
  isLeft,
  isActive,
  isCompleted,
  statusColors,
  statusLabels,
}: TimelineItemProps) {
  const { ref, isInView } = useInView<HTMLDivElement>(0.15);

  const slideDirection = isLeft ? '-translate-x-8' : 'translate-x-8';
  const mdSlideDirection = isLeft ? 'md:-translate-x-10' : 'md:translate-x-10';

  return (
    <div
      ref={ref}
      className={`relative flex items-start md:items-center transition-all duration-700 ease-out ${
        isInView
          ? `opacity-100 translate-y-0 ${mdSlideDirection}`
          : `opacity-0 translate-y-8 ${slideDirection} ${mdSlideDirection}`
      }`}
      style={{ transitionDelay: `${index * 120}ms` }}
    >
      {/* Dot on the line */}
      <div
        className={`absolute left-4 md:left-1/2 w-4 h-4 rounded-full border-4 border-white md:-translate-x-2 z-10 transition-all duration-500 ${
          isInView ? 'scale-100' : 'scale-0'
        } ${statusColors[event.status]}`}
        style={{
          transitionDelay: `${index * 120 + 200}ms`,
          boxShadow: isActive
            ? '0 0 0 4px rgba(217, 119, 6, 0.2), 0 0 20px rgba(217, 119, 6, 0.5)'
            : isCompleted
            ? '0 0 0 3px rgba(16, 185, 129, 0.15)'
            : 'none',
        }}
      >
        {/* Pulse ring for active items */}
        {isActive && (
          <span className="absolute inset-0 rounded-full bg-accent animate-ping opacity-40" />
        )}
      </div>

      {/* Connector line from dot to card (desktop only) */}
      <div
        className={`hidden md:block absolute top-1/2 h-px bg-gray-300 transition-all duration-700 ${
          isInView ? 'opacity-100 w-8' : 'opacity-0 w-0'
        } ${isLeft ? 'right-[55%] mr-1' : 'left-[55%] ml-1'}`}
        style={{ transitionDelay: `${index * 120 + 300}ms` }}
      />

      {/* Card */}
      <div
        className={`ml-12 md:ml-0 md:w-[45%] ${
          isLeft ? 'md:mr-auto md:pr-12' : 'md:ml-auto md:pl-12'
        }`}
      >
        <div
          className={`bg-white border rounded-xl p-5 transition-all duration-500 hover:shadow-lg ${
            isActive
              ? 'border-accent/30 shadow-md shadow-accent/5'
              : 'border-gray-200 hover:border-gray-300'
          }`}
        >
          <div className="flex items-center gap-2 mb-2">
            <span
              className={`px-2.5 py-1 rounded-full text-xs font-semibold ${
                isCompleted
                  ? 'bg-emerald-50 text-emerald-700'
                  : isActive
                  ? 'bg-accent/10 text-accent-dark'
                  : 'bg-gray-100 text-gray-500'
              }`}
            >
              {event.date}
            </span>
            <span
              className={`text-xs font-medium ${
                isCompleted
                  ? 'text-emerald-600'
                  : isActive
                  ? 'text-accent'
                  : 'text-gray-400'
              }`}
            >
              {statusLabels[event.status]}
            </span>
          </div>
          <h3 className="font-bold text-primary text-base mb-1">{event.phase}</h3>
          <p className="text-gray-600 text-sm leading-relaxed">{event.description}</p>
        </div>
      </div>
    </div>
  );
}